import React, { useCallback } from 'react';
import {
  Box, Button, ButtonText, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { useLocalParticipant } from '@livekit/components-react';
import { MonitorUp } from 'lucide-react-native';
import { Track } from 'livekit-client';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';

import { useCarriedScreenShare } from '@/student/module/hooks/useCarriedScreenShare';

import type { LobbyStepProps } from './types';

/**
 * Lobby step ③ — mandatory screen share (student UX §2.3 / AC-SS1).
 * Blocks until a screen track is published; leave-only decline policy (no bypass).
 *
 * Capture goes through {@link useCarriedScreenShare} so the gesture-granted display
 * track is retained above the room-remount boundary and survives the pre-flight →
 * exam room switch. A clone is published into the current (pre-flight) room so the
 * lecturer sees the screen during briefing.
 */
export function LobbyScreenShareStep({ onComplete }: LobbyStepProps) {
  const { t } = useTranslation();
  const isWeb = Platform.OS === 'web';
  const { capture, cloneTrack, hasScreen } = useCarriedScreenShare();
  const { localParticipant } = useLocalParticipant();

  const isPublished = hasScreen;

  const handleStartShare = useCallback(async () => {
    const ok = await capture();
    if (!ok || !localParticipant) return;
    // Show the freshly-granted screen to the lecturer during briefing by
    // publishing a clone into the current pre-flight room (replacing any prior).
    try {
      const existing = localParticipant.getTrackPublication(Track.Source.ScreenShare);
      if (existing?.track) {
        await localParticipant.unpublishTrack(existing.track, true);
      }
      const carried = cloneTrack();
      if (carried) {
        await localParticipant.publishTrack(carried, {
          source: Track.Source.ScreenShare,
          name: 'screen',
        });
      }
    } catch {
      // Publishing into the pre-flight room is best-effort; the carry itself
      // (used for the exam room) already succeeded, which is what gates progress.
    }
  }, [capture, cloneTrack, localParticipant]);

  return (
    <VStack space="lg" p="$4" alignItems="center" testID="oral-exam-lobby-screenshare-step">
      <VStack space="xs" alignItems="center" maxWidth={520}>
        <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
          {t('oralExam.lobby.screenShare.title')}
        </Text>
        <Text size="md" color="$textBody" textAlign="center">
          {t('oralExam.lobby.screenShare.body')}
        </Text>
      </VStack>

      <Box
        width="100%"
        maxWidth={520}
        minHeight={200}
        borderRadius="$lg"
        borderWidth={1}
        borderColor="$borderLight"
        bg="$bgSubtle"
        alignItems="center"
        justifyContent="center"
        p="$4"
      >
        {isPublished ? (
          <VStack space="sm" alignItems="center" testID="oral-exam-lobby-screenshare-confirm">
            <Icon as={MonitorUp} size="xl" color="$statusSuccess" />
            <Text size="md" fontWeight="semibold" color="$textHeading" textAlign="center">
              {t('oralExam.lobby.screenShare.confirmTitle')}
            </Text>
            <Text size="sm" color="$textBody" textAlign="center">
              {t('oralExam.lobby.screenShare.confirmBody')}
            </Text>
            {isWeb ? (
              <Button
                size="sm"
                variant="outline"
                onPress={handleStartShare}
                testID="oral-exam-lobby-screenshare-change"
              >
                <ButtonText>{t('oralExam.lobby.screenShare.change')}</ButtonText>
              </Button>
            ) : null}
          </VStack>
        ) : (
          <VStack space="md" alignItems="center">
            <Icon as={MonitorUp} size="xl" color="$textSubtle" />
            <Text size="sm" color="$textSubtle" textAlign="center">
              {t('oralExam.lobby.screenShare.placeholder')}
            </Text>
            {isWeb ? (
              <Button
                size="md"
                action="primary"
                onPress={handleStartShare}
                testID="oral-exam-lobby-screenshare-start"
              >
                <HStack space="sm" alignItems="center">
                  <Icon as={MonitorUp} size="sm" color="$textInverted" />
                  <ButtonText>{t('oralExam.lobby.screenShare.start')}</ButtonText>
                </HStack>
              </Button>
            ) : (
              <Text size="sm" color="$textSubtle" textAlign="center">
                {t('oralExam.lobby.screenShare.webOnly')}
              </Text>
            )}
          </VStack>
        )}
      </Box>

      <Button
        size="md"
        action="primary"
        onPress={() => {
          if (isPublished) onComplete();
        }}
        isDisabled={!isPublished}
        testID="oral-exam-lobby-screenshare-continue"
        maxWidth={320}
        width="100%"
      >
        <ButtonText>{t('oralExam.lobby.screenShare.continue')}</ButtonText>
      </Button>
    </VStack>
  );
}
